import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'react-feather';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RouteNames } from '../../constants';
import DottedQRCode from '../MembershipCard/DottedQRCode';
import styles from './SessionPopup.module.scss';

/**
 * "Download the app" popup for signed-out visitors. Mounted once in
 * CustomerLayout, so it covers Home and every page that uses the
 * customer header (Search, Businesses, Online Brands, coupon/business
 * details, …).
 *
 * Shows once per browser tab, after GUEST_TIME_LIMIT_MS of time spent
 * signed out on those pages. Only time while the tab is visible counts,
 * and the count carries across page changes (sessionStorage), so moving
 * between pages doesn't restart it. Closing it (✕, "Continue with web" —
 * which also goes to the customer sign-in page —
 * backdrop or Esc) stops it coming back for the rest of the tab session.
 *
 * Styled to match the "Log in to continue" popup (LoginPrompt): same
 * pink card, red outlined close button and two-column layout with the
 * image panel on the left (hidden on narrow screens).
 */

const GUEST_TIME_LIMIT_MS = 60 * 1000;
const TICK_MS = 1000;
const ELAPSED_KEY = 'SESSION_POPUP_ELAPSED_MS';
const DISMISSED_KEY = 'SESSION_POPUP_DISMISSED';

// Same smart link the landing page's store buttons use — it sends each
// phone to the right store (App Store / Google Play).
const APP_DOWNLOAD_URL = 'https://couponbook.app.link/BsnZ5PEfH2b';

const MOCKUP_SRC = `${process.env.PUBLIC_URL}/images/session-popup/app-mockup.webp`;

const readNumber = key => {
  try {
    return Number(sessionStorage.getItem(key)) || 0;
  } catch (e) {
    return 0;
  }
};

const isDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.storeIcon}>
    <path
      fill="#fff"
      d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
    />
  </svg>
);

const GooglePlayIcon = () => (
  <svg viewBox="0 0 24 26" aria-hidden="true" className={styles.storeIcon}>
    <path fill="#00A0FF" d="M1.2.6 13.6 13 1.2 25.4c-.3-.3-.5-.8-.5-1.4V2c0-.6.2-1.1.5-1.4Z" />
    <path fill="#00D86F" d="M1.2.6c.4-.4 1.1-.4 1.8 0l14.8 8.2L13.6 13Z" />
    <path fill="#FFC400" d="m17.8 8.8 4.5 2.5c1.3.7 1.3 2.7 0 3.4l-4.5 2.5-4.2-4.2Z" />
    <path fill="#F5394B" d="m13.6 13 4.2 4.2L3 25.4c-.7.4-1.4.4-1.8 0Z" />
  </svg>
);

const SessionPopup = () => {
  const authorized = useSelector(s => !!(s.auth && s.auth.authorized));
  const isRestoringAuth = useSelector(
    s => !!(s.auth && s.auth.persist && s.auth.persist.restore && s.auth.persist.restore.fetching),
  );
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  // Closed during this page load — keeps it closed even if sessionStorage
  // is unavailable and the dismissed flag couldn't be saved.
  const [closedThisLoad, setClosedThisLoad] = useState(false);

  // Count visible, signed-out time; open once the limit is reached.
  useEffect(() => {
    if (authorized || isRestoringAuth || isOpen || closedThisLoad || isDismissed()) return undefined;

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const elapsed = readNumber(ELAPSED_KEY) + TICK_MS;
      try {
        sessionStorage.setItem(ELAPSED_KEY, String(elapsed));
      } catch (e) {
        // Storage unavailable (private mode etc.) — the in-memory interval
        // still runs; the count just won't survive a page reload.
      }
      if (elapsed >= GUEST_TIME_LIMIT_MS) setIsOpen(true);
    };

    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [authorized, isRestoringAuth, isOpen, closedThisLoad]);

  // Signing in while it's open closes it.
  useEffect(() => {
    if (authorized) setIsOpen(false);
  }, [authorized]);

  const close = useCallback(() => {
    setIsOpen(false);
    setClosedThisLoad(true);
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
      // ignore — worst case it can show again after a reload
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={close}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-popup-title"
        onClick={e => e.stopPropagation()}
      >
        <button type="button" aria-label="Close" className={styles.close} onClick={close}>
          <X size={30} />
        </button>

        <div className={styles.imageCol}>
          <img src={MOCKUP_SRC} alt="My Couponbook app screens" className={styles.image} />
        </div>

        <div className={styles.contentCol}>
          <h2 id="session-popup-title" className={styles.title}>
            <span className={styles.titleBig}>Download</span>
            <span className={styles.titleLine}>
              <span className={styles.titleRed}>My Couponbook</span> App for
            </span>
            <span className={styles.titleLine}>the best experience in hand.</span>
          </h2>

          <div className={styles.qrCard}>
            <DottedQRCode value={APP_DOWNLOAD_URL} size={200} color="#2B2B2B" title="Scan to download the My Couponbook app" />
          </div>

          <div className={styles.storeRow}>
            <a className={styles.storeBtn} href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
              <AppleIcon />
              <span className={styles.storeText}>
                <span className={styles.storeSmall}>Download on the</span>
                <span className={styles.storeBig}>App Store</span>
              </span>
            </a>
            <a className={styles.storeBtn} href={APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
              <GooglePlayIcon />
              <span className={styles.storeText}>
                <span className={`${styles.storeSmall} ${styles.storeSmallCaps}`}>GET IT ON</span>
                <span className={styles.storeBig}>Google Play</span>
              </span>
            </a>
          </div>

          <p className={styles.or}>Or</p>

          <button
            type="button"
            className={styles.webBtn}
            onClick={() => {
              close();
              navigate(RouteNames.SIGN_IN);
            }}
          >
            Continue with web
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SessionPopup;
